/**
 * Shared styling + helpers for gov workspace pages (Tasks / Contacts /
 * Documents Library / Financials). Keeps the pages congruent with
 * GovDashboardPage / GovSolicitationsPage / GovConstructionJobsPage.
 */
import React from 'react';

export const GOV_COLORS = {
  bg: '#0F1923',
  card: '#1A2B3C',
  cardHi: '#243447',
  border: '#243447',
  accent: '#4A7EB5',
  text: '#E5EDF5',
  textMuted: '#8FA8C0',
  textDim: '#526980',
  danger: '#EF4444',
  warning: '#F97316',
  caution: '#EAB308',
  success: '#22C55E',
  info: '#3B82F6',
} as const;

export const S: Record<string, React.CSSProperties> = {
  container: { padding: 24, background: GOV_COLORS.bg, minHeight: '100vh', color: GOV_COLORS.text },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 26, fontWeight: 800, margin: 0, color: '#fff' },
  subtitle: { color: GOV_COLORS.textMuted, fontSize: 14, marginTop: 4 },
  toolbar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const },
  input: {
    padding: '9px 12px', background: '#0F1923', border: `1px solid ${GOV_COLORS.border}`,
    borderRadius: 8, color: GOV_COLORS.text, fontSize: 13, outline: 'none', minWidth: 220,
  },
  select: {
    padding: '9px 12px', background: '#0F1923', border: `1px solid ${GOV_COLORS.border}`,
    borderRadius: 8, color: GOV_COLORS.text, fontSize: 13, outline: 'none', cursor: 'pointer',
  },
  addBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '9px 18px', background: GOV_COLORS.accent, color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  cancelBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '9px 18px', background: 'transparent', color: GOV_COLORS.textMuted,
    border: `1px solid ${GOV_COLORS.border}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '9px 18px', background: 'transparent', color: GOV_COLORS.danger,
    border: `1px solid ${GOV_COLORS.danger}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  card: { background: GOV_COLORS.card, padding: 16, borderRadius: 10, border: `1px solid ${GOV_COLORS.border}` },
  listRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
    background: GOV_COLORS.card, borderRadius: 8, border: `1px solid ${GOV_COLORS.border}`,
    cursor: 'pointer', transition: 'background 0.1s',
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
    borderRadius: 12, fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
  },
  empty: { color: GOV_COLORS.textDim, fontStyle: 'italic', padding: 40, textAlign: 'center' as const },
  loading: { color: GOV_COLORS.textMuted, padding: 40, textAlign: 'center' as const },
  panelTitle: { fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, marginBottom: 14 },
  kvLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' as const, color: GOV_COLORS.textMuted, marginBottom: 4 },
  kvValue: { fontSize: 14, color: '#fff', fontWeight: 500 },
  modalBackdrop: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: GOV_COLORS.card, borderRadius: 12, padding: 24,
    width: 560, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' as const,
    border: `1px solid ${GOV_COLORS.border}`,
  },
  modalTitle: { fontSize: 18, fontWeight: 800, color: '#fff', margin: 0, marginBottom: 20 },
  modalRow: { marginBottom: 14 },
  modalLabel: { display: 'block', color: GOV_COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' as const, marginBottom: 6 },
  modalFooter: { display: 'flex', gap: 10, marginTop: 20, alignItems: 'stretch' },
};

// Deadline coloring
export function deadlineColor(days: number): string {
  if (days < 7) return GOV_COLORS.danger;
  if (days < 14) return GOV_COLORS.warning;
  if (days < 30) return GOV_COLORS.caution;
  return GOV_COLORS.accent;
}

export function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((then - now) / (1000 * 60 * 60 * 24));
}

export function fmtMoney(n?: number | null): string {
  if (n == null) return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
}

export function fmtMoneyFull(n?: number | null): string {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const CONTACT_TYPES: Array<{ key: string; label: string; icon: string; color: string }> = [
  { key: 'contracting_officer',   label: 'Contracting Officer',    icon: '🎯', color: '#EF4444' },
  { key: 'contracting_specialist',label: 'Contracting Specialist', icon: '📋', color: '#F97316' },
  { key: 'cor',                   label: 'COR',                    icon: '👁️', color: '#EAB308' },
  { key: 'poc',                   label: 'POC',                    icon: '📞', color: '#3B82F6' },
  { key: 'end_user',              label: 'End User',               icon: '🏛️', color: '#6366F1' },
  { key: 'small_biz_specialist',  label: 'Small Biz Specialist',   icon: '🤝', color: '#8B5CF6' },
  { key: 'teaming_partner',       label: 'Teaming Partner',        icon: '🤝', color: '#10B981' },
  { key: 'subcontractor',         label: 'Subcontractor',          icon: '🔧', color: '#22C55E' },
  { key: 'supplier',              label: 'Supplier',               icon: '📦', color: '#84CC16' },
  { key: 'bonding_agent',         label: 'Bonding Agent',          icon: '🔒', color: '#0EA5E9' },
  { key: 'insurance_agent',       label: 'Insurance Agent',        icon: '🛡️', color: '#06B6D4' },
  { key: 'past_perf_reference',   label: 'Past Perf Reference',    icon: '⭐', color: '#EC4899' },
  { key: 'other',                 label: 'Other',                  icon: '👤', color: '#64748B' },
];

// Cost-analysis-specific tokens & helpers (V3.4)
export const COST_TYPE_META: Record<string, { label: string; icon: string; color: string }> = {
  labor:        { label: 'Labor',        icon: '👷', color: '#EAB308' },
  material:     { label: 'Material',     icon: '🧱', color: '#3B82F6' },
  equipment:    { label: 'Equipment',    icon: '🔨', color: '#F97316' },
  subcontract:  { label: 'Subcontract',  icon: '📄', color: '#8B5CF6' },
  mobilization: { label: 'Mobilization', icon: '🚚', color: '#06B6D4' },
  travel:       { label: 'Travel',       icon: '✈️',  color: '#0EA5E9' },
  disposal:     { label: 'Disposal',     icon: '🗑️',  color: '#84CC16' },
  permit:       { label: 'Permit',       icon: '📜', color: '#EC4899' },
  testing:      { label: 'Testing',      icon: '🧪', color: '#22C55E' },
  bond:         { label: 'Bond',         icon: '🔒', color: '#EF4444' },
  other:        { label: 'Other',        icon: '📄', color: '#64748B' },
};

export const PRICING_METHOD_META: Record<string, { label: string; confidenceTier: 'high' | 'medium' | 'low' }> = {
  solicitation_quantity: { label: 'Solicitation Qty',   confidenceTier: 'high' },
  vendor_quote:          { label: 'Vendor Quote',       confidenceTier: 'high' },
  historical_actual:     { label: 'Historical Actual',  confidenceTier: 'high' },
  internal_cost_library: { label: 'Cost Library',       confidenceTier: 'medium' },
  drawing_takeoff:       { label: 'Drawing Takeoff',    confidenceTier: 'medium' },
  published_bid_tab:     { label: 'Published Bid Tab',  confidenceTier: 'medium' },
  local_supplier:        { label: 'Local Supplier',     confidenceTier: 'medium' },
  ai_extraction:         { label: 'AI Extraction',      confidenceTier: 'medium' },
  market_research:       { label: 'Market Research',    confidenceTier: 'low' },
  allowance:             { label: 'Allowance',          confidenceTier: 'low' },
  estimator_override:    { label: 'Estimator Override', confidenceTier: 'low' },
};

export const RISK_SEVERITY_META: Record<string, { label: string; color: string }> = {
  low:      { label: 'Low',      color: '#22C55E' },
  medium:   { label: 'Medium',   color: '#EAB308' },
  high:     { label: 'High',     color: '#F97316' },
  critical: { label: 'Critical', color: '#EF4444' },
};

export function confidenceColor(score: number | null | undefined): string {
  if (score == null) return GOV_COLORS.textDim;
  if (score >= 80) return GOV_COLORS.success;
  if (score >= 60) return GOV_COLORS.caution;
  if (score >= 40) return GOV_COLORS.warning;
  return GOV_COLORS.danger;
}

export function confidenceLabel(score: number | null | undefined): string {
  if (score == null) return 'Unknown';
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium';
  if (score >= 40) return 'Low';
  return 'Very Low';
}

export function statusMeta(status: string): { label: string; color: string; icon: string } {
  switch (status) {
    case 'processing':  return { label: 'Processing', color: GOV_COLORS.accent,  icon: '⏳' };
    case 'draft':       return { label: 'Draft',      color: GOV_COLORS.caution, icon: '✏️' };
    case 'final':       return { label: 'Final',      color: GOV_COLORS.success, icon: '✅' };
    case 'stale':       return { label: 'Stale',      color: GOV_COLORS.warning, icon: '⚠️' };
    case 'superseded':  return { label: 'Superseded', color: GOV_COLORS.textDim, icon: '🗄️' };
    case 'failed':      return { label: 'Failed',     color: GOV_COLORS.danger,  icon: '❌' };
    default:            return { label: status,       color: GOV_COLORS.textMuted, icon: '•' };
  }
}

export const DOC_CATEGORIES: Array<{ key: string; label: string; icon: string }> = [
  { key: 'solicitation',         label: 'Solicitation (SF1449/SF33)', icon: '📑' },
  { key: 'sow_pws',              label: 'SOW / PWS',                  icon: '📋' },
  { key: 'wage_determination',   label: 'Wage Determination',         icon: '💵' },
  { key: 'drawings',             label: 'Drawings / Plans',           icon: '📐' },
  { key: 'attachment',           label: 'Attachment',                 icon: '📎' },
  { key: 'amendment',            label: 'Amendment',                  icon: '📝' },
  { key: 'qa',                   label: 'Q&A',                        icon: '❓' },
  { key: 'site_visit',           label: 'Site Visit',                 icon: '📍' },
  { key: 'bond_form',            label: 'Bond Form',                  icon: '🔒' },
  { key: 'past_performance',     label: 'Past Performance',           icon: '⭐' },
  { key: 'response',             label: 'Response',                   icon: '📤' },
  { key: 'capability_statement', label: 'Capability Statement',       icon: '📊' },
  { key: 'internal',             label: 'Internal',                   icon: '🗄️' },
  { key: 'other',                label: 'Other',                      icon: '📄' },
];
